<template>
  <!-- Figma "Service & Repair - Details - Members & Contacts" (5515:3664): breadcrumb, members card + Quote Recipient card.
       Data: bookings/show (consumer = primary contact) + consumer/member/list (same list Android FamilyMembersDialog shows). -->
  <main class="jobMembersPg w-full">
    <EvalShell title="Members at this Address" :job-id="jobId" :show-details="false" :base-crumbs="baseCrumbs"
      :trail="[{ label: 'Consumer' }, { label: 'Members' }]">
      <template #title-extra>
        <!-- no add-member endpoint in api/v3/pro yet (backend gap) -->
        <button type="button" disabled
          class="hidden items-center gap-[8px] h-[36px] pl-[4px] pr-[14px] rounded-full border-1 border-[#E9ECF1] bg-white font-roboto text-[14px] text-[#333333] disabled:opacity-[0.5] disabled:cursor-not-allowed">
          <span class="w-[28px] h-[28px] rounded-full flex items-center justify-center bg-[#14C38E]">
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M7 1.5v11M1.5 7h11" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round" />
            </svg>
          </span>
          Add Member
        </button>
      </template>

      <p class="mb-[10px] text-[13px] leading-[18px] font-roboto text-[#8A9099]">
        Anyone listed here can approve a quote and receive the invoice for this property.
      </p>

      <p v-if="loaded && !rows.length" class="m-0 py-[20px] text-center text-[14px] font-roboto text-[#8A9099]">
        {{ errorMessage || 'No Members Found.' }}
      </p>

      <ul v-else class="list-none m-0 p-0 flex flex-col gap-[10px] font-roboto">
        <li v-for="(row, index) in rows" :key="row.key"
          class="flex flex-wrap items-center gap-x-[16px] gap-y-[12px] px-[18px] py-[14px] rounded-[10px] border-1"
          :class="row.primary ? 'bg-[#F7F8FD] border-[#C6CCF0]' : 'bg-[#FFFFFF] border-[#E9ECF1]'">
          <div class="flex-1 min-w-[220px] flex items-center gap-[14px]">
            <span
              class="w-[45px] largeDesktop:w-[50px] h-[45px] largeDesktop:h-[50px] rounded-full flex-shrink-0 flex items-center justify-center text-[16px] font-medium text-white"
              :style="{ backgroundColor: AVATAR_COLORS[index % AVATAR_COLORS.length] }">
              {{ row.initial }}
            </span>
            <div class="min-w-0 flex flex-col gap-[4px]">
              <div class="flex flex-wrap items-center gap-[8px]">
                <span
                  class="text-[18px] largeDesktop:text-[20px] leading-[20px] font-semibold text-[#333333] break-words">{{
                    row.name }}</span>
                <span v-if="row.primary"
                  class="px-[13px] py-[5px] rounded-[6px] bg-[#EDEFFB] text-[12px] font-semibold text-[#6C79E0]">Primary</span>
              </div>
              <div
                class="flex flex-wrap items-center gap-x-[24px] gap-y-[2px] text-[13px] leading-[18px] text-[#666666]">
                <span v-if="row.role" class="mobile:basis-full tablet:basis-auto text-[#999999]">{{ row.role }}</span>
                <span v-if="row.phone">{{ row.phone }}</span>
                <span v-if="row.email" class="break-all">{{ row.email }}</span>
              </div>
            </div>
          </div>

          <div class="flex flex-wrap items-center gap-[10px] mobile:pl-[54px] tablet:pl-0">
            <a v-if="row.canCall" :href="'tel:' + telOf(row.phone)" :aria-label="'Call ' + row.name"
              class="w-[30px] h-[30px] rounded-full flex-shrink-0 flex items-center justify-center bg-[#F67280]">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 32 32">
                <path d="M0 0h32v32H0z" fill="none" />
                <path fill="#fff" d="M24 13h-2a3.003 3.003 0 0 0-3-3V8a5.006 5.006 0 0 1 5 5" />
                <path fill="#fff"
                  d="M28 13h-2a7.01 7.01 0 0 0-7-7V4a9.01 9.01 0 0 1 9 9m-7.667 8.482l2.24-2.24a2.17 2.17 0 0 1 2.337-.48l2.728 1.092A2.17 2.17 0 0 1 29 21.866v4.961a2.167 2.167 0 0 1-2.284 2.169C7.594 27.806 3.732 11.61 3.015 5.408A2.162 2.162 0 0 1 5.169 3h4.873a2.17 2.17 0 0 1 2.012 1.362l1.091 2.728a2.17 2.17 0 0 1-.48 2.337l-2.24 2.24s1.242 8.732 9.908 9.815" />
              </svg>

            </a>
            <a v-if="row.email" :href="'mailto:' + row.email" :aria-label="'Email ' + row.name"
              class="w-[30px] h-[30px] rounded-full flex-shrink-0 flex items-center justify-center bg-[#168EFF]">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                <path d="M0 0h24v24H0z" fill="none" />
                <path fill="#fff" fill-rule="evenodd"
                  d="m22 6.86l-10 5.775L2 6.86v-.11A2.755 2.755 0 0 1 4.75 4h14.5A2.755 2.755 0 0 1 22 6.75zm-10 7.505L2 8.595v8.655A2.755 2.755 0 0 0 4.75 20h10.225a4.5 4.5 0 0 1-.475-2c0-2.48 2.02-4.5 4.5-4.5c1.155 0 2.205.44 3 1.155V8.59zM19 15c1.655 0 3 1.345 3 3s-1.345 3-3 3s-3-1.345-3-3s1.345-3 3-3"
                  clip-rule="evenodd" />
              </svg>

            </a>
            <!-- no set-primary endpoint yet (backend gap) -->
            <button v-if="!row.primary" type="button" disabled
              class="hidden h-[30px] px-[10px] rounded-[5px] border-1 border-[#7695FF] bg-white text-[12px] text-[#7695FF] whitespace-nowrap disabled:opacity-[0.5] disabled:cursor-not-allowed">
              Set as primary
            </button>
            <!-- no edit / remove member endpoint yet (backend gap) -->
            <button type="button" disabled :aria-label="'More actions for ' + row.name"
              :class="row.primary ? 'bg-[#fff]' : 'bg-[#F7F8FD]'"
              class="hidden w-[30px] h-[30px] rounded-full flex-shrink-0 items-center justify-center  text-[#8A9099] disabled:cursor-not-allowed">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 1024 1024">
                <path d="M0 0h1024v1024H0z" fill="none" />
                <path fill="#8a9099"
                  d="M456 231a56 56 0 1 0 112 0a56 56 0 1 0-112 0m0 280a56 56 0 1 0 112 0a56 56 0 1 0-112 0m0 280a56 56 0 1 0 112 0a56 56 0 1 0-112 0" />
              </svg>

            </button>
          </div>
        </li>
      </ul>

      <template #aside>
        <div class="flex items-center gap-1 pb-[15px]">
          <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8]"></div>
          <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8]"></div>
          <p class="text-[18px] laptop:text-[20px] font-semibold font-roboto pl-2 m-0 text-[#333333]">Quote Recipient
          </p>
        </div>
        <p class="m-0 mb-[15px] text-[13px] leading-[18px] font-roboto text-[#8A9099]">
          The approval request and invoice are sent to the primary contact unless you pick someone else.
        </p>
        <!-- read-only until the booking has a quote-recipient field/endpoint (backend gap) -->
        <FxSelect v-model="recipient" :options="recipientOptions" disabled button-class="disabled:opacity-[0.7]" />
      </template>
    </EvalShell>
  </main>
</template>

<script>
import EvalShell from "@/components/nest_components/EvalShell.vue";
import FxSelect from "@/components/partialComponent/FxSelect.vue";
import { consumerMembersApi } from "@/services/consumerMembersApi";
import { isSilentLogin, isFromMobile } from "@/helpers/embedMode";
import { useToast } from "vue-toastification";
const toast = useToast();

const AVATAR_COLORS = ['#6C79E0', '#2EC4A9', '#F5A524', '#F67280', '#A084E8'];

export default {
  components: { EvalShell, FxSelect },
  data() {
    return { AVATAR_COLORS, consumer: null, members: [], loaded: false, errorMessage: '', recipient: 'primary' };
  },
  computed: {
    recipientOptions() {
      return this.rows.map(row => ({ label: row.name + (row.primary ? " (Primary)" : ""), value: row.key }));
    },
    jobId() {
      return this.$route.params.id;
    },
    // also opened from Appointment Details (route appointment-members): appointment crumbs and consumer
    isAppointment() {
      return this.$route.name === 'appointment-members';
    },
    // and from Universal Quote Details (route quote-members)
    isQuote() {
      return this.$route.name === 'quote-members';
    },
    baseCrumbs() {
      if (this.isQuote) {
        const list = [];
        if (!isSilentLogin()) list.push({ label: 'Universal Quotes', to: '/work-order/universal-quotes' });
        if (!isFromMobile(this.$route)) list.push({ label: 'Quote Details', to: '/quote/detail/' + this.jobId });
        return list;
      }
      if (!this.isAppointment) return null;
      const list = [];
      if (!isSilentLogin()) list.push({ label: 'Appointments', to: '/work-order/appointment' });
      // the mobile app webview has its own back navigation: no Appointment Details crumb
      if (!isFromMobile(this.$route)) list.push({ label: 'Appointment Details', to: '/appointment/detail/' + this.jobId });
      return list;
    },
    // booking consumer = primary contact (has email); member_info rows have no email or primary flag
    rows() {
      const rows = [];
      if (this.consumer) {
        const name = this.consumer.full_name || [this.consumer.first_name, this.consumer.last_name].filter(Boolean).join(' ');
        rows.push({
          key: 'primary', primary: true, name, initial: this.initialOf(name), role: 'Primary Contact',
          phone: this.consumer.phone || '', email: this.consumer.email || '', canCall: !!this.consumer.phone,
        });
      }
      this.members.forEach((m) => {
        const name = [m.first_name, m.last_name].filter(Boolean).join(' ');
        rows.push({
          key: 'm-' + m.member_id, primary: false, name, initial: this.initialOf(name), role: m.relation || '',
          phone: m.phone || '', email: '',
          // same rule as the old modal / Android FamilyMembersAdapter: call only when the consumer allowed it
          canCall: !!m.is_allowed && !!m.phone,
        });
      });
      return rows;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      const booking = this.isQuote ? await consumerMembersApi.quote(this.jobId)
        : this.isAppointment ? await consumerMembersApi.appointment(this.jobId) : await consumerMembersApi.booking(this.jobId);
      if (!booking.ok || !booking.body?.consumer) {
        this.errorMessage = booking.message || `Unable to load the consumer for this ${this.isQuote ? 'quote' : this.isAppointment ? 'appointment' : 'job'}.`;
        toast.error(this.errorMessage);
        this.loaded = true;
        return;
      }
      this.consumer = booking.body.consumer;
      const res = await consumerMembersApi.list(this.consumer.user_id);
      if (res.ok) {
        this.members = Array.isArray(res.body) ? res.body : [];
      } else if (res.message) {
        toast.error(res.message);
      }
      this.loaded = true;
    },
    initialOf(name) {
      return (name || '?').trim().charAt(0).toUpperCase() || '?';
    },
    telOf(phone) {
      return String(phone).replace(/[^\d+]/g, '');
    },
  },
};
</script>
